import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Search, Download, Building2, User as UserIcon, Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteViewingConfirmation,
  useUploadViewingConfirmation,
  useViewingConfirmations,
  type ViewingConfirmation,
} from '@/hooks/data/useViewingConfirmations';
import { useProperties } from '@/hooks/data/useProperties';
import { useClients } from '@/hooks/data/useClients';
import { useConfirm } from '@/components/ui/confirm';
import { formatDate, formatDateTime, formatNumber } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/viewing-confirmations')({
  component: ViewingConfirmationsPage,
});

function ViewingConfirmationsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detail, setDetail] = useState<ViewingConfirmation | null>(null);

  const { data: items = [], isLoading } = useViewingConfirmations();
  const { data: properties = [] } = useProperties();
  const { data: clients = [] } = useClients();
  const propertyMap = useMemo(() => new Map(properties.map((p) => [p.id, p])), [properties]);
  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => {
      const prop = propertyMap.get(it.property_id);
      const client = it.client_id ? clientMap.get(it.client_id) : null;
      const haystack = [
        prop?.address,
        prop?.city,
        client?.first_name,
        client?.last_name,
        it.file_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(s);
    });
  }, [items, search, propertyMap, clientMap]);

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('viewing.title', 'Besichtigungsschein')}
          </h2>
          <p className="text-sm text-ink-3">
            {t(
              'viewing.subtitle',
              'Unterschriebene Besichtigungsbestätigungen archivieren und durchsuchen.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
        >
          <Upload className="h-4 w-4" />
          {t('viewing.upload.cta', 'Hochladen')}
        </button>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('viewing.stats.total', 'Gesamt archiviert')}</p>
          <p className="fm-kpi-value">{formatNumber(items.length)}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('viewing.stats.month', 'Diesen Monat')}</p>
          <p className="fm-kpi-value">
            {
              items.filter((it) => {
                const d = new Date(it.created_at);
                const now = new Date();
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
              }).length
            }
          </p>
        </div>
        <div className="fm-kpi !border-ink !bg-ink">
          <p className="fm-kpi-label !text-gold">{t('viewing.stats.signed', 'Unterschrieben')}</p>
          <p className="fm-kpi-value !text-paper">
            {items.filter((it) => it.signed_at).length}
          </p>
        </div>
      </div>

      <div className="fm-card p-5 space-y-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              'viewing.search.placeholder',
              'Suchen nach Adresse, Klient, Datei …',
            )}
            className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="fm-table">
            <thead>
              <tr>
                <th>{t('viewing.cols.property', 'Immobilie')}</th>
                <th>{t('viewing.cols.client', 'Klient')}</th>
                <th>{t('viewing.cols.file', 'Datei')}</th>
                <th>{t('viewing.cols.uploaded', 'Hochgeladen')}</th>
                <th className="text-end">{t('viewing.cols.actions', 'Aktion')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="text-center text-ink-3">
                    {t('common.loading', 'Lädt …')}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-ink-3">
                    <FileText className="mx-auto mb-2 h-8 w-8 text-line-strong" />
                    {t('viewing.empty', 'Keine Besichtigungsbestätigungen.')}
                  </td>
                </tr>
              ) : (
                filtered.map((it) => (
                  <Row
                    key={it.id}
                    item={it}
                    propertyMap={propertyMap}
                    clientMap={clientMap}
                    isAdmin={isAdmin}
                    onOpen={() => setDetail(it)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        properties={properties}
        clients={clients}
      />
      <DetailDialog
        item={detail}
        propertyMap={propertyMap}
        clientMap={clientMap}
        onOpenChange={(o) => !o && setDetail(null)}
      />
    </section>
  );
}

function Row({
  item,
  propertyMap,
  clientMap,
  isAdmin,
  onOpen,
}: {
  item: ViewingConfirmation;
  propertyMap: Map<string, { address: string | null; city: string }>;
  clientMap: Map<
    string,
    { title: string | null; first_name: string | null; last_name: string }
  >;
  isAdmin: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const del = useDeleteViewingConfirmation();
  const prop = propertyMap.get(item.property_id);
  const client = item.client_id ? clientMap.get(item.client_id) : null;
  const clientName = client
    ? [client.title, client.first_name, client.last_name].filter(Boolean).join(' ')
    : '—';

  const onDelete = async () => {
    const ok = await confirm({
      title: t('viewing.delete.title', 'Bestätigung löschen?'),
      body: item.file_name,
      confirmLabel: t('common.delete', 'Löschen'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(item.id);
      toast.success(t('viewing.delete.done', 'Gelöscht.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <tr className="cursor-pointer" onClick={onOpen}>
      <td>
        <p className="font-medium text-ink truncate">{prop?.address ?? '—'}</p>
        <p className="text-xs text-ink-3 truncate">{prop?.city ?? ''}</p>
      </td>
      <td>{clientName}</td>
      <td>
        <p className="font-mono text-xs text-ink-2 truncate max-w-[280px]">
          {item.file_name}
        </p>
        {item.file_size && (
          <p className="text-[10px] text-ink-3">{Math.round(item.file_size / 1024)} kB</p>
        )}
      </td>
      <td className="text-xs text-ink-3">
        {formatDate(new Date(item.created_at))}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1">
          <a
            href={`r2://${item.file_key}`}
            title={t('viewing.actions.download', 'Datei')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2 cursor-not-allowed opacity-60"
            onClick={(e) => {
              e.preventDefault();
              toast.info(
                t(
                  'viewing.cdn_pending',
                  'Direkter Download wird in v2 wenn CDN-Domain konfiguriert ist verfügbar.',
                ),
              );
            }}
          >
            <Download className="h-3.5 w-3.5" />
          </a>
          {isAdmin && (
            <button
              type="button"
              onClick={onDelete}
              disabled={del.isPending}
              title={t('viewing.actions.delete', 'Löschen')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))] disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function UploadDialog({
  open,
  onOpenChange,
  properties,
  clients,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  properties: Array<{ id: string; address: string | null; city: string }>;
  clients: Array<{
    id: string;
    title: string | null;
    first_name: string | null;
    last_name: string;
    phone: string;
    client_type: string;
  }>;
}) {
  const { t } = useTranslation();
  const upload = useUploadViewingConfirmation();
  const [propertyId, setPropertyId] = useState('');
  const [clientId, setClientId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  if (!open) return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !propertyId) return;
    try {
      await upload.mutateAsync({ file, propertyId, clientId: clientId || null });
      toast.success(t('viewing.upload.done', 'Hochgeladen.'));
      onOpenChange(false);
      setPropertyId('');
      setClientId('');
      setFile(null);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-line bg-card p-6 shadow-strong">
        <h3 className="mb-4 font-display text-lg text-ink">
          {t('viewing.upload.title', 'Besichtigungsschein hochladen')}
        </h3>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase text-ink-3 tracking-[0.08em]">
              {t('viewing.upload.property', 'Immobilie')} *
            </label>
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              required
              className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            >
              <option value="">— {t('viewing.upload.choose', 'wählen')} —</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.address ?? p.city}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase text-ink-3 tracking-[0.08em]">
              {t('viewing.upload.client', 'Klient (Käufer)')}
            </label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            >
              <option value="">— optional —</option>
              {clients
                .filter((c) => c.client_type === 'kupac')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.title, c.first_name, c.last_name].filter(Boolean).join(' ')} · {c.phone}
                  </option>
                ))}
            </select>
          </div>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line-strong bg-paper-2 px-6 py-8 text-center transition hover:border-gold hover:bg-card">
            <Upload className="h-6 w-6 text-ink-3" />
            <p className="text-sm font-medium text-ink">
              {file
                ? file.name
                : t('viewing.upload.dropzone', 'PDF / JPG / PNG hier ablegen oder wählen')}
            </p>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center gap-2 rounded-md border border-line px-4 py-2 text-sm font-medium hover:bg-paper-2"
            >
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button
              type="submit"
              disabled={!file || !propertyId || upload.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-ink px-4 py-2 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" />
              {upload.isPending
                ? t('common.saving', 'Speichern …')
                : t('viewing.upload.submit', 'Hochladen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetailDialog({
  item,
  propertyMap,
  clientMap,
  onOpenChange,
}: {
  item: ViewingConfirmation | null;
  propertyMap: Map<string, { address: string | null; city: string }>;
  clientMap: Map<
    string,
    { title: string | null; first_name: string | null; last_name: string; phone?: string }
  >;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!item) return null;
  const prop = propertyMap.get(item.property_id);
  const client = item.client_id ? clientMap.get(item.client_id) : null;
  const clientName = client
    ? [client.title, client.first_name, client.last_name].filter(Boolean).join(' ')
    : '—';

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">
            {t('viewing.detail.title', 'Besichtigungsbestätigung')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field
            icon={<Building2 className="h-4 w-4" />}
            label={t('viewing.detail.property', 'Immobilie')}
            value={prop ? `${prop.address ?? '—'} · ${prop.city}` : '—'}
            onClick={
              prop
                ? () => {
                    onOpenChange(false);
                    void navigate({ to: '/properties' });
                  }
                : undefined
            }
          />
          <Field
            icon={<UserIcon className="h-4 w-4" />}
            label={t('viewing.detail.client', 'Klient')}
            value={clientName}
          />
          <Field
            icon={<FileText className="h-4 w-4" />}
            label={t('viewing.detail.file', 'Datei')}
            value={
              <div>
                <p className="break-all font-mono text-sm text-ink">{item.file_name}</p>
                {item.file_size && (
                  <p className="text-xs text-ink-3">
                    {Math.round(item.file_size / 1024)} kB · {item.mime_type ?? '—'}
                  </p>
                )}
              </div>
            }
          />
          <Field
            icon={<Calendar className="h-4 w-4" />}
            label={t('viewing.detail.uploaded', 'Hochgeladen')}
            value={formatDateTime(new Date(item.created_at))}
          />
          {item.signed_at && (
            <Field
              icon={<FileText className="h-4 w-4" />}
              label={t('viewing.detail.signed', 'Unterschrieben')}
              value={formatDateTime(new Date(item.signed_at))}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  icon,
  label,
  value,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="flex w-full items-start gap-3 rounded-md border border-line bg-paper-2 p-3 text-start disabled:cursor-default"
    >
      <span className="mt-0.5 text-ink-3">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase text-ink-3 tracking-[0.06em]">{label}</p>
        <div className="mt-1 text-sm text-ink">{value}</div>
      </div>
    </button>
  );
}
